import { useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Phone, MapPin, Clock, Check } from "lucide-react";
import { AVO, IMAGES, PRODUCTS, GALLERY } from "@/data/content";
import { MaskLine, Reveal } from "@/components/Motion";
import Marquee from "@/components/Marquee";

const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

const NavB = () => (
  <header className="absolute inset-x-0 top-0 z-40 flex items-center justify-between px-6 py-7 md:px-14">
    <button
      data-testid="concept-b-logo"
      onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
      className="block h-7 w-auto overflow-hidden rounded-sm sm:h-9"
      aria-label="AVO Fence & Supply"
    >
      <img src="/avo-logo.png" alt="AVO Fence & Supply" className="h-full w-auto" />
    </button>
    <nav className="hidden items-center gap-10 font-cormorant text-sm uppercase tracking-[0.3em] text-[#635F59] md:flex">
      {[
        ["Collection", "collection-b", "nav-link-products"],
        ["Heritage", "heritage-b", "nav-link-about"],
        ["Gallery", "gallery-b", "nav-link-gallery"],
        ["Consultation", "consultation-b", "nav-link-contact"],
      ].map(([label, id, tid]) => (
        <button key={id} data-testid={tid} onClick={() => scrollTo(id)} className="transition-colors hover:text-[#1C1B1A]">
          {label}
        </button>
      ))}
    </nav>
    <a
      data-testid="concept-b-phone-link"
      href={AVO.phoneHref}
      className="hidden border-b border-[#8B7768] pb-1 font-cormorant text-sm tracking-[0.15em] text-[#1C1B1A] transition-colors hover:border-[#1C1B1A] sm:inline-block"
    >
      {AVO.phone}
    </a>
  </header>
);

const HeroB = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);

  return (
    <section ref={ref} className="relative grid min-h-screen items-center gap-10 px-6 pb-32 pt-24 md:grid-cols-[1.15fr_1fr] md:px-14 md:pb-20 md:pt-32 lg:gap-20">
      <div>
        <MaskLine delay={0.15}>
          <span className="font-cormorant text-xs font-semibold uppercase tracking-[0.35em] text-[#8B7768]">
            Established in Stoughton, Massachusetts
          </span>
        </MaskLine>
        <h1 className="mt-8 font-cormorant font-medium leading-[1.02] tracking-tight text-[#1C1B1A]">
          <MaskLine delay={0.3} className="text-5xl md:text-6xl lg:text-7xl">Artisanal cedar</MaskLine>
          <MaskLine delay={0.45} className="text-5xl md:text-6xl lg:text-7xl">
            <em className="text-[#B87E1F]">architecture</em> for
          </MaskLine>
          <MaskLine delay={0.6} className="text-5xl md:text-6xl lg:text-7xl">fine estates.</MaskLine>
        </h1>
        <MaskLine delay={0.8}>
          <p className="mt-8 max-w-md text-base leading-relaxed text-[#635F59]">
            Supplying custom timber fencing, estate gates, and handcrafted garden structures to New
            England's most distinguished properties — direct from our Stoughton yard.
          </p>
        </MaskLine>
        <MaskLine delay={0.95}>
          <div className="mt-10 flex flex-wrap items-center gap-6">
            <button
              data-testid="hero-request-sample-button"
              onClick={() => scrollTo("consultation-b")}
              className="group inline-flex items-center gap-3 rounded-full bg-[#1C1B1A] px-8 py-4 font-cormorant text-sm font-semibold uppercase tracking-[0.25em] text-[#F7F6F2] transition-colors duration-300 hover:bg-[#B87E1F] hover:text-[#1C1B1A]"
            >
              Request a Consultation
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <button
              data-testid="hero-view-gallery-button"
              onClick={() => scrollTo("gallery-b")}
              className="border-b border-[#1C1B1A] pb-1 font-cormorant text-sm uppercase tracking-[0.25em] text-[#1C1B1A] transition-colors hover:border-[#8B7768] hover:text-[#8B7768]"
            >
              View the Gallery
            </button>
          </div>
        </MaskLine>
        <Reveal delay={1.1} className="mt-12 flex gap-6 border-t border-[#E5E2D9] pt-8 md:mt-16 md:gap-12">
          {[
            ["60+", "Years of craft"],
            ["No. 1", "Cedar inventory in New England"],
            ["USA", "Proudly milled"],
          ].map(([v, l]) => (
            <div key={l}>
              <p className="font-cormorant text-3xl font-medium text-[#1C1B1A] md:text-4xl">{v}</p>
              <p className="mt-1 font-cormorant text-[10px] uppercase tracking-[0.2em] text-[#8B7768] md:text-xs md:tracking-[0.25em]">{l}</p>
            </div>
          ))}
        </Reveal>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 60 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1.2, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
        className="relative mx-auto w-full max-w-md md:max-w-none"
      >
        <div className="overflow-hidden rounded-t-[999px] border border-[#E5E2D9] bg-white p-3 shadow-2xl shadow-[#1C1B1A]/10">
          <div className="overflow-hidden rounded-t-[999px]">
            <motion.img
              src={IMAGES.arbor}
              alt="Handcrafted cedar arbor"
              style={{ y: imgY }}
              className="aspect-[3/4] w-full scale-110 object-cover"
            />
          </div>
        </div>
        <p className="mt-4 text-center font-cormorant text-sm italic text-[#8B7768]">
          Northern White Cedar — the quiet signature of a well-kept estate
        </p>
      </motion.div>
    </section>
  );
};

const CollectionB = () => (
  <section id="collection-b" className="px-6 py-24 md:px-14 md:py-36">
    <Reveal className="flex flex-wrap items-end justify-between gap-6">
      <div>
        <p className="font-cormorant text-xs font-semibold uppercase tracking-[0.35em] text-[#8B7768]">The Collection</p>
        <h2 className="mt-4 max-w-2xl font-cormorant text-4xl font-medium leading-[1.05] tracking-tight text-[#1C1B1A] md:text-6xl">
          Six crafts, <em className="text-[#B87E1F]">one timber.</em>
        </h2>
      </div>
      <p className="max-w-xs text-sm leading-relaxed text-[#635F59]">
        Every piece begins as Northern White Cedar at our Stoughton mill — then becomes something
        worth keeping.
      </p>
    </Reveal>

    <div className="mt-16 grid gap-x-8 gap-y-16 md:grid-cols-2 lg:grid-cols-3">
      {PRODUCTS.map((p, i) => (
        <Reveal key={p.id} delay={(i % 3) * 0.1} className={i % 3 === 1 ? "lg:mt-16" : ""}>
          <article data-testid={`product-category-card-${p.id}`} className="group">
            <div className="relative overflow-hidden">
              <img
                src={p.image}
                alt={p.name}
                className="aspect-[4/5] w-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-[1.06]"
              />
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#F4B032]/50 via-[#F4B032]/10 to-transparent opacity-0 transition-opacity duration-700 group-hover:opacity-100" />
              <span className="absolute left-4 top-4 bg-[#F7F6F2]/90 px-3 py-1 font-cormorant text-xs uppercase tracking-[0.25em] text-[#1C1B1A] backdrop-blur transition-colors duration-500 group-hover:bg-[#F4B032] group-hover:text-[#1C1B1A]">
                {String(i + 1).padStart(2, "0")}
              </span>
            </div>
            <div className="relative mt-5 flex items-baseline justify-between gap-4 border-b border-[#E5E2D9] pb-4">
              <h3 className="font-cormorant text-2xl font-semibold text-[#1C1B1A] transition-colors duration-500 group-hover:text-[#B87E1F]">{p.name}</h3>
              <span className="font-cormorant text-xs italic text-[#8B7768]">{p.tag}</span>
              <span className="absolute -bottom-px left-0 h-px w-0 bg-[#B87E1F] transition-[width] duration-700 ease-out group-hover:w-full" />
            </div>
            <p className="mt-3 text-sm leading-relaxed text-[#635F59]">{p.description}</p>
          </article>
        </Reveal>
      ))}
    </div>
  </section>
);

const HeritageB = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-6%", "6%"]);

  return (
    <section id="heritage-b" ref={ref} className="bg-[#1C1B1A] px-6 py-24 md:px-14 md:py-36">
      <div className="grid items-center gap-14 lg:grid-cols-2 lg:gap-24">
        <div>
          <Reveal>
            <p className="font-cormorant text-xs font-semibold uppercase tracking-[0.35em] text-[#B87E1F]">Our Heritage</p>
            <h2 className="mt-4 font-cormorant text-4xl font-medium leading-[1.05] tracking-tight text-[#F7F6F2] md:text-5xl">
              For decades, New England has built with <em className="text-[#B87E1F]">Avo cedar.</em>
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="mt-8 max-w-lg text-base leading-relaxed text-[#F7F6F2]/70">{AVO.about}</p>
          </Reveal>
          <Reveal delay={0.25}>
            <blockquote className="mt-10 border-l-2 border-[#B87E1F] pl-6 font-cormorant text-2xl italic leading-snug text-[#F7F6F2]">
              "We supply the finest materials — you create the space. Supply only, by design."
            </blockquote>
          </Reveal>
          <Reveal delay={0.35}>
            <div className="mt-10 grid grid-cols-3 gap-6 border-t border-[#F7F6F2]/15 pt-8">
              {[
                ["Homeowners", "Direct pricing"],
                ["Installers", "Reliable stock"],
                ["Landscapers", "Volume accounts"],
              ].map(([t, s]) => (
                <div key={t}>
                  <p className="font-cormorant text-lg font-semibold text-[#F7F6F2]">{t}</p>
                  <p className="mt-1 text-xs uppercase tracking-[0.2em] text-[#B87E1F]">{s}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
        <Reveal delay={0.2}>
          <div className="overflow-hidden">
            <motion.img
              src={IMAGES.workshop}
              alt="Craftsman workshop"
              style={{ y: imgY }}
              className="aspect-[4/5] w-full scale-110 object-cover"
            />
          </div>
          <p className="mt-4 text-right font-cormorant text-sm italic text-[#B87E1F]">The mill yard, Stoughton MA</p>
        </Reveal>
      </div>
    </section>
  );
};

const GalleryB = () => (
  <section id="gallery-b" className="px-6 py-24 md:px-14 md:py-36">
    <Reveal className="text-center">
      <p className="font-cormorant text-xs font-semibold uppercase tracking-[0.35em] text-[#8B7768]">The Gallery</p>
      <h2 className="mt-4 font-cormorant text-4xl font-medium tracking-tight text-[#1C1B1A] md:text-5xl">
        Quietly standing, <em className="text-[#B87E1F]">season after season.</em>
      </h2>
    </Reveal>
    <div className="mt-16 grid gap-10 md:grid-cols-3">
      {GALLERY.slice(0, 3).map((g, i) => (
        <Reveal key={g.caption} delay={i * 0.12} className={i === 1 ? "md:mt-14" : ""}>
          <figure className="group">
            <div className="overflow-hidden border border-[#E5E2D9] bg-white p-2.5 shadow-lg shadow-[#1C1B1A]/5">
              <img
                src={g.image}
                alt={g.caption}
                className="aspect-[3/4] w-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-[1.05]"
              />
            </div>
            <figcaption className="mt-4 text-center font-cormorant text-base italic text-[#635F59]">{g.caption}</figcaption>
          </figure>
        </Reveal>
      ))}
    </div>
  </section>
);

const ConsultationB = () => {
  const [form, setForm] = useState({ name: "", email: "", type: "Estate Fencing", footage: 150, message: "" });
  const [done, setDone] = useState(false);

  const inputCls =
    "w-full border border-[#E5E2D9] bg-white px-5 py-4 text-sm text-[#1C1B1A] placeholder:text-[#635F59]/50 focus:border-[#1C1B1A] focus:outline-none transition-colors";

  return (
    <section id="consultation-b" className="bg-[#EFECE6] px-6 py-24 md:px-14 md:py-36">
      <div className="mx-auto grid max-w-6xl gap-14 lg:grid-cols-[1fr_1.1fr] lg:gap-24">
        <Reveal>
          <p className="font-cormorant text-xs font-semibold uppercase tracking-[0.35em] text-[#8B7768]">Consultation</p>
          <h2 className="mt-4 font-cormorant text-4xl font-medium leading-[1.05] tracking-tight text-[#1C1B1A] md:text-5xl">
            Begin with a <em className="text-[#B87E1F]">conversation.</em>
          </h2>
          <p className="mt-8 max-w-md text-base leading-relaxed text-[#635F59]">
            Tell us about your property and your project. Our team will prepare a material
            recommendation and quote — and, if you'd like, connect you with a trusted local
            installer.
          </p>
          <div className="mt-10 space-y-4">
            {["Complimentary material consultation", "Sample cedar finishes available at the yard", "Delivery throughout New England"].map((t) => (
              <p key={t} className="flex items-center gap-3 text-sm text-[#635F59]">
                <Check className="h-4 w-4 text-[#B87E1F]" /> {t}
              </p>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="border border-[#E5E2D9] bg-white p-8 shadow-xl shadow-[#1C1B1A]/5 md:p-10">
            {done ? (
              <div data-testid="consultation-success-message" className="flex min-h-[360px] flex-col items-center justify-center text-center">
                <span className="flex h-14 w-14 items-center justify-center rounded-full bg-[#1C1B1A]">
                  <Check className="h-6 w-6 text-[#F7F6F2]" />
                </span>
                <h3 className="mt-6 font-cormorant text-3xl font-semibold text-[#1C1B1A]">With pleasure.</h3>
                <p className="mt-3 max-w-xs text-sm leading-relaxed text-[#635F59]">
                  Thank you{form.name ? `, ${form.name}` : ""}. A member of our yard team will reach
                  out within one business day.
                </p>
              </div>
            ) : (
              <form
                onSubmit={(e) => { e.preventDefault(); setDone(true); }}
                className="space-y-5"
              >
                <div className="grid gap-5 md:grid-cols-2">
                  <input
                    data-testid="consultation-name-input"
                    placeholder="Full name"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className={inputCls}
                  />
                  <input
                    data-testid="consultation-email-input"
                    type="email"
                    placeholder="Email address"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className={inputCls}
                  />
                </div>
                <select
                  data-testid="consultation-project-type-select"
                  value={form.type}
                  onChange={(e) => setForm({ ...form, type: e.target.value })}
                  className={inputCls}
                >
                  {["Estate Fencing", "Entrance Gate", "Pergola or Arbor", "Posts & Accents", "Full Property Package"].map((t) => (
                    <option key={t}>{t}</option>
                  ))}
                </select>
                <div>
                  <div className="flex items-baseline justify-between">
                    <label className="font-cormorant text-xs font-semibold uppercase tracking-[0.25em] text-[#8B7768]">
                      Approximate footage
                    </label>
                    <span className="font-cormorant text-2xl font-semibold text-[#1C1B1A]">{form.footage} ft</span>
                  </div>
                  <input
                    data-testid="consultation-footage-slider"
                    type="range" min="20" max="800" step="10" value={form.footage}
                    onChange={(e) => setForm({ ...form, footage: Number(e.target.value) })}
                    className="mt-3 w-full accent-[#1C1B1A]"
                  />
                </div>
                <textarea
                  data-testid="consultation-message-textarea"
                  placeholder="Tell us about your property…"
                  rows={3}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  className={`${inputCls} resize-none`}
                />
                <button
                  data-testid="quote-form-submit-button"
                  type="submit"
                  className="group flex w-full items-center justify-center gap-3 rounded-full bg-[#1C1B1A] py-4 font-cormorant text-sm font-semibold uppercase tracking-[0.25em] text-[#F7F6F2] transition-colors hover:bg-[#B87E1F] hover:text-[#1C1B1A]"
                >
                  Request Consultation
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </button>
              </form>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
};

const FooterB = () => (
  <footer className="bg-[#1C1B1A] px-6 pb-28 pt-16 text-[#F7F6F2] md:px-14">
    <div className="flex flex-col items-start justify-between gap-10 md:flex-row md:items-end">
      <img
        src="/avo-logo.png"
        alt="AVO Fence & Supply"
        data-testid="footer-b-logo"
        className="h-12 w-auto md:h-16"
      />
      <p className="font-cormorant text-xs uppercase tracking-[0.3em] text-[#8B7768]">Custom Fencing · Craftsman Quality</p>
    </div>
    <div className="mt-14 grid gap-10 border-t border-white/10 pt-10 md:grid-cols-3">
      <div>
        <p className="font-cormorant text-xs font-semibold uppercase tracking-[0.3em] text-[#B87E1F]">The Yard</p>
        <p className="mt-3 flex items-start gap-2 text-sm text-[#F7F6F2]/70">
          <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-[#B87E1F]" />
          {AVO.address}, {AVO.city}
        </p>
        <a href={AVO.phoneHref} className="mt-2 flex items-center gap-2 text-sm text-[#F7F6F2] hover:text-[#B87E1F]">
          <Phone className="h-4 w-4 text-[#B87E1F]" /> {AVO.phone}
        </a>
      </div>
      <div>
        <p className="font-cormorant text-xs font-semibold uppercase tracking-[0.3em] text-[#B87E1F]">Hours</p>
        <ul className="mt-3 space-y-2">
          {AVO.hours.map((h) => (
            <li key={h.days} className="flex items-center gap-2 text-sm text-[#F7F6F2]/70">
              <Clock className="h-3.5 w-3.5 shrink-0 text-[#B87E1F]" />
              <span className="w-32 text-[#F7F6F2]">{h.days}</span> {h.time}
            </li>
          ))}
        </ul>
      </div>
      <div>
        <p className="font-cormorant text-xs font-semibold uppercase tracking-[0.3em] text-[#B87E1F]">Our Promise</p>
        <p className="mt-3 text-sm leading-relaxed text-[#F7F6F2]/70">
          The largest selection of cedar fencing in New England — all products proudly made in the
          U.S.A.
        </p>
      </div>
    </div>
    <div className="mt-12 flex flex-wrap items-center justify-between gap-4 border-t border-white/10 pt-6">
      <p className="text-[11px] uppercase tracking-[0.2em] text-[#F7F6F2]/50">© 2026 AVO Fence &amp; Supply</p>
      <p className="text-[11px] uppercase tracking-[0.2em] text-[#F7F6F2]/50">Concept B — Light Architectural</p>
    </div>
  </footer>
);

const ConceptB = () => (
  <div data-testid="concept-b-page" className="concept-b-selection min-h-screen bg-[#F7F6F2] pb-10 font-jakarta">
    <NavB />
    <HeroB />
    <Marquee
      items={["Northern White Cedar", "Estate Entrance Gates", "Custom Architectural Pergolas", "Post & Rail Systems", "New England Craftsmanship"]}
      className="bg-[#1C1B1A] py-6 text-[#F7F6F2]"
      itemClassName="font-cormorant text-xl italic tracking-[0.1em] md:text-2xl"
      speed={55}
    />
    <CollectionB />
    <HeritageB />
    <GalleryB />
    <ConsultationB />
    <FooterB />
  </div>
);

export default ConceptB;
